<script setup lang="ts">
import SearchSelect from "../../ui/form/SearchSelect.vue";
import TextInput from "../../ui/form/TextInput.vue";
import CheckInput from "../../ui/form/CheckInput.vue";
import GridInput from "../../ui/form/GridInput.vue";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../components/ui/Templates/ModalFormTemplate.vue";
import { useFormValidation } from "../../../composables/useFormValidation";
import { required } from "../../../utils/validation";
import { api } from "../../../api/Api";
import {
  Condition,
  Field,
  Join,
  ReportDesign,
  Table,
  OperationOptions,
  MatchTypeOptions,
  ConditionTypeOptions,
  JoinTypeOptions,
} from "../../../types/report";
import Toast from "../../../services/Toast";
import IconComponent from "../../ui/common/IconComponent.vue";
import Buttons from "../../ui/form/Buttons.vue";
import { mapSqlTypeToVueType } from "../../../types/mapSqlTypeToVueType";
import TextAreaInput from "../../ui/form/TextAreaInput.vue";

const props = defineProps<{
  report?: ReportDesign;
  title: string;
  data?: any;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
}>();

const emit = defineEmits<{
  (e: "selectTable", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  name: props.data?.name || "",
  displayName: props.data?.displayName || "",
  alias: props.data?.alias || "",
  fields: props.data?.fields || [],
  baseTable: props.data?.baseTable || false,
  sourceField: props.data?.sourceField || "",
  joinType: props.data?.joinType || "",
  targetTableId: props.data?.targetTableId || "",
  targetTableName: props.data?.targetTableName || "",
  targetTableAlias: props.data?.targetTableAlias || "",
  targetField: props.data?.targetField || "",
  joins: props.data?.joins || [],
  conditions: props.data?.conditions || [],
  raw: props.data?.raw || false,
  rawQuery: props.data?.rawQuery || "",
});

setFieldRules("id", [required("Table is required")]);
setFieldRules("name", [required("Table Name is required")]);
setFieldRules("alias", [required("Table Alias is required")]);
setFieldRules(
  "joinType",
  !fields.value.baseTable.value ? [required("Join Type is required")] : []
);
setFieldRules(
  "sourceField",
  !fields.value.baseTable.value ? [required("Source Field is required")] : []
);
setFieldRules(
  "targetTableId",
  !fields.value.baseTable.value ? [required("Target Table is required")] : []
);
setFieldRules(
  "targetField",
  !fields.value.baseTable.value ? [required("Target Field is required")] : []
);

const handleSubmit = async () => {
  setFieldRules(
  "name",
  !fields.value.raw.value ? [required("Table Name is required")] : []
);
setFieldRules(
  "rawQuery",
  fields.value.raw.value ? [required("SQL Query is required")] : []
);
if (fields.value.raw.value) {
  fields.value.id.value = "join_" + Math.random().toString(36).substring(2, 15);
}
console.log("Fields:", fields.value);
console.log("Form Data:", formData.value);
  if (validateForm()) {    
    emit("selectTable", formData.value);
  }
};

const handleTableSelect = async (table: any) => {
  console.log(table);
  if (table) {
    const fld = await getFields(table);
    fields.value.id.value =
      "table_" + Math.random().toString(36).substring(2, 15);
    fields.value.name.value = table.id;
    fields.value.displayName.value = table.name;
    fields.value.alias.value = table.name
      .replace(/[_-\s]+/g, "_")
      .toLowerCase();
    fields.value.fields.value = fld || [];
    getSourceFields(table);
  } else {
    fields.value.id.value = "";
    fields.value.name.value = "";
    fields.value.displayName.value = "";
    fields.value.alias.value = "";
    fields.value.fields.value = [];
    sourceFields.value = [];
  }
};

const getFields = async (table: Table) => {
  try {
    const response = await api.post("/clients/reports/fields", {
      table: table.id,
    });
    if (response.success == true) {
      let data = response.data;
      data.forEach((field: { field_type: string; }) => {
        field.field_type = mapSqlTypeToVueType(field.field_type);
      });
      return data;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Source Fields", "error");
    return [];
  }
};

const getSourceFields = async (table: Table) => {
  try {
    const response = await api.post("/clients/reports/fields", {
      table: table.id,
    });
    if (response.success == true) {
      let data = response.data;
      data.forEach((field: { field_type: string; }) => {
        field.field_type = mapSqlTypeToVueType(field.field_type);
      });
      sourceFields.value = data;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Source Fields", "error");
  }
};

const sourceFields = ref<Field[]>([]);
const targetFields = ref<Field[]>([]);

const handleTargetTableSelect = (table: any, join?: Join) => {
  if (join) {
    join.targetTableId = table.id;
    join.targetTableName = table.name;
    join.targetTableAlias = table.alias || table.name;
    getTargetFields(table, join);
  } else if (table) {
    fields.value.targetTableName.value = table.name;
    fields.value.targetTableAlias.value = table.alias || table.name;
    getTargetFields(table);
  } else {
    formData.value.targetTableId = "";
    formData.value.targetTableName = "";
    formData.value.targetTableAlias = "";
    targetFields.value = [];
  }
};

const handleConditionTableSelect = (table: any, condition: Condition) => {
  console.log("join", condition);
  console.log("tabler", table);
  if (condition && table) {
    condition.tableId = table.id;
    condition.tableName = table.name;
    condition.tableAlias = table.alias || table.name;
    getTargetFields(table, undefined, condition);
  } else if (condition) {
    condition.tableId = "";
    condition.tableName = "";
    condition.tableAlias = "";
    condition.tableFields = [];
  }
};

const handleConditionDbTableSelect = (table: any, condition: Condition) => {
  console.log("join", condition);
  console.log("tabler", table);
  if (condition && table) {
    condition.dbTableId = table.id;
    condition.dbTable = table.name;
    condition.dbTableAlias = table.alias || table.name;
    getTargetFields(table, undefined, undefined, condition);
  } else if (condition) {
    condition.dbTableId = "";
    condition.dbTable = "";
    condition.dbTableAlias = "";
    condition.dbTableFields = [];
  }
};

const getTargetFields = async (
  table: Table,
  join?: Join,
  condition?: Condition,
  conditionDb?: Condition
) => {
  try {
    const response = await api.post("/clients/reports/fields", {
      table: table.name,
    });
    if (response.success == true) {
      let data = response.data;
      data.forEach((field: { field_type: string; }) => {
        field.field_type = mapSqlTypeToVueType(field.field_type);
      });
      if (join) {
        join.targetTableFields = data;
      } else if (condition) {
        condition.tableFields = data;
      } else if (conditionDb) {
        conditionDb.dbTableFields = data;
      } else {
        targetFields.value = data;
      }
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Source Fields", "error");
  }
};

const handleAddJoin = () => {
  
  if (!validateForm()) {
    Toast.show("Please fill all required fields", "error");
    return;
  }

  let valid = true;
  formData.value.joins.forEach((join: Join) => {
    if (!join.targetField) {
      valid = false;
      join.targetFieldError = "Target Field is required";
    } else {
      join.targetFieldError = "";
    }
    if (!join.targetTableId) {
      valid = false;
      join.targetTableError = "Target Table is required";
    } else {
      join.targetTableError = "";
    }
    if (!join.joinType) {
      valid = false;
      join.joinTypeError = "Join Type is required";
    } else {
      join.joinTypeError = "";
    }
    if (!join.sourceField) {
      valid = false;
      join.sourceFieldError = "Source Field is required";
    } else {
      join.sourceFieldError = "";
    }
  });
  if (!valid) {
    Toast.show("Please fill all required fields in joins", "error");
    return;
  }
  console.log("Form Data:", formData.value);
  if (fields.value.targetField.value && fields.value.targetTableId.value) {
    const join = {
      id: "join_" + Math.random().toString(36).substring(2, 15),
      sourceField: "",
      sourceTableId: fields.value.id.value,
      sourceTableName: fields.value.name.value,
      sourceTableAlias: fields.value.alias.value,
      operator: "",
      joinType: "",
      targetTableId: "",
      targetTableName: "",
      targetTableAlias: "",
      targetField: "",
      targetTableFields: [],
    };
    formData.value.joins.push(join);
  } else {
    Toast.show("Please select all required fields", "error");
  }
};

const removeJoin = (index: number) => {
  formData.value.joins.splice(index, 1);
};

const removeCondition = (index: number) => {
  formData.value.conditions.splice(index, 1);
};

const removeChildCondition = (condition: Condition, index: number) => {
  condition?.childConditions?.splice(index, 1);
};

const handleAddCondition = () => {
  if (fields.value.targetField.value && fields.value.targetTableId.value) {
    const condition: Condition = {
      id: "condition_" + Math.random().toString(36).substring(2, 15),
      type: undefined,
      tableId: "",
      tableName: "",
      tableAlias: fields.value.alias.value,
      fieldName: "",
      operator: undefined,
      matchType: "Value",
      dbTable: "",
      dbTableAlias: "",
      dbField: "",
      value: "",
      group: false,
      childConditions: [],
    };
    formData.value.conditions.push(condition);
  } else {
    Toast.show("Please select all required fields", "error");
  }
};

const handleConditionGroupChange = (value: boolean, condition: Condition) => {
  if (value) {
    condition.group = true;
    const cond: Condition = {
      id: "condition_" + Math.random().toString(36).substring(2, 15),
      type: undefined,
      tableId: "",
      tableName: "",
      tableAlias: fields.value.alias.value,
      fieldName: "",
      operator: undefined,
      matchType: "Value",
      dbTable: "",
      dbTableAlias: "",
      dbField: "",
      value: "",
      group: false,
      childConditions: [],
    };
    condition.childConditions = [];
    condition.childConditions.push(cond);
    condition.childConditions.push(cond);
  } else {
    condition.group = false;
    condition.childConditions = [];
  }
};

const handleAddChildCondition = (condition: Condition) => {
  if (condition) {
    const cond: Condition = {
      id: "condition_" + Math.random().toString(36).substring(2, 15),
      type: undefined,
      tableId: "",
      tableName: "",
      tableAlias: fields.value.alias.value,
      fieldName: "",
      operator: undefined,
      matchType: "Value",
      dbTable: "",
      dbTableAlias: "",
      dbField: "",
      value: "",
      group: false,
      childConditions: [],
    };
    condition.childConditions?.push(cond);
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    setFormData(props.data);
  }
});
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    endPoint=""
    :perm="[]"
    :tabList="[]"
    :formData="formData"
    :ignore-permission="true"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-5 gap-6">
        <CheckInput
          v-model="fields.raw.value"
          label="SQL Query"
          class="col-span-1"
          :readonly="isReadOnly" />
        <SearchSelect
          label="Table"
          v-model="fields.name.value"
          v-if="!fields.raw.value"
          class="col-span-2"
          @select="handleTableSelect"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
          custom-url="reports/tables"
          placeholder="Select Table"
        />

        <TextInput
          v-model="fields.alias.value"
          label="Table Alias"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.alias.touched ? fields.alias.error : null"
          @blur="fields.alias.touched = true"
        />

        <TextAreaInput
            v-model="fields.rawQuery.value"
            label="SQL Query"
            class="w-full col-span-5"
            v-if="fields.raw.value"
            :readonly="isReadOnly"
            :error="fields.rawQuery.touched ? fields.rawQuery.error : null"
            @blur="fields.rawQuery.touched = true" />

      </div>
      <GridInput label="Joins">
        <div class="grid md:grid-cols-4 gap-6">
          <TextInput
          v-model="fields.sourceField.value"
          label="Source Field"
          v-if="fields.raw.value"
          :readonly="isReadOnly"
          :error="fields.sourceField.touched ? fields.sourceField.error : null"
          @blur="fields.sourceField.touched = true"
        />
          <SearchSelect
            label="Source Field"
            v-model="fields.sourceField.value"
            v-if="!fields.raw.value"
            :error="
              fields.sourceField.touched ? fields.sourceField.error : null
            "
            @blur="fields.sourceField.touched = true"
            :options="sourceFields"
          />

          <SearchSelect
            label="Join Type"
            v-model="fields.joinType.value"
            :error="fields.joinType.touched ? fields.joinType.error : null"
            :options="JoinTypeOptions"
          />

          <SearchSelect
            label="Target Table"
            v-model="fields.targetTableId.value"
            @select="handleTargetTableSelect"
            :error="
              fields.targetTableId.touched ? fields.targetTableId.error : null
            "
            @blur="fields.targetTableId.touched = true"
            :options="report?.tables"
            placeholder="Select Table"
          />

          <SearchSelect
            label="Target Field"
            v-model="fields.targetField.value"
            :error="
              fields.targetField.touched ? fields.targetField.error : null
            "
            :options="targetFields"
            placeholder="Select Table"
          />
        </div>
        <div
          class="grid md:grid-cols-4 gap-6"
          v-for="(join, index) in formData.joins"
        >
          <SearchSelect
            label="Source Field"
            v-model="join.sourceField"
            :error="join.sourceFieldError"
            :options="sourceFields"
          />
          <SearchSelect
            label="Join Type"
            v-model="join.joinType"
            :error="join.joinTypeError"
            :options="[
              { name: 'INNER JOIN', id: 'INNER JOIN' },
              { name: 'LEF JOIN', id: 'LEF JOIN' },
              { name: 'RIGHT JOIN', id: 'RIGHT JOIN' },
              { name: 'CROSS JOIN', id: 'CROSS JOIN' },
            ]"
          />
          <SearchSelect
            v-model="join.targetTableId"
            @select="handleTargetTableSelect($event, join)"
            :error="join.targetTableError"
            :options="report?.tables"
            label="Target Table"
            :readonly="isReadOnly"
          />
          <div class="flex gap-1">
            <SearchSelect
              label="Target Field"
              v-model="join.targetField"
              :options="join.targetTableFields"
              :error="join.targetFieldError"
              placeholder="Select Field"
            />
            <button
              id="table-action-remove-table"
              @click="removeJoin(index)"
              class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600"
              title="Remvoe Join"
            >
              <IconComponent name="Trash2" />
            </button>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Buttons
            caption="Add Joins"
            type="info"
            icon="Merge"
            @click="handleAddJoin"
            v-if="mode != 'view'"
          />
        </div>
      </GridInput>

      <GridInput label="Conditions">
        <div class="col-span-4 mt-2">
          <div
            class="grid md:grid-cols-4 gap-6"
            :class="{
              'border border-gray-300 dark:border-gray-600 p-4 rounded-lg mt-1':
                formData.conditions.length > 0,
            }"
            v-for="(condition, index) in formData.conditions"
          >
            <SearchSelect
              v-model="condition.type"
              :error="condition.typeError"
              v-if="index > 0"
              :options="ConditionTypeOptions"
              label="Type"
              :readonly="isReadOnly"
            />

            <CheckInput
              v-model="condition.group"
              @change="handleConditionGroupChange($event, condition)"
              label="Group"
              class="col-span-1"
            />

            <!--<SearchSelect
              v-model="condition.tableId"
              @select="handleConditionTableSelect($event, condition)"
              v-if="!condition.group"
              :error="condition.tableError"
              :options="report?.tables"
              label="Table"
              :readonly="isReadOnly"
            />
            -->
            <TextInput
              v-model="condition.tableAlias"
              v-if="!condition.group"
              :error="condition.tableError"
              :options="report?.tables"
              label="Table"
              :readonly="isReadOnly" />

            <SearchSelect
              label="Field"
              v-model="condition.fieldName"
              v-if="!condition.group"
              :error="condition.fieldNameError"
              :options="sourceFields"
            />
            <SearchSelect
              label="Operator"
              v-model="condition.operator"
              v-if="!condition.group"
              :error="condition.operatorError"
              :options="OperationOptions"
            />
            <SearchSelect
              label="Match Type"
              v-model="condition.matchType"
              v-if="!condition.group"
              :options="MatchTypeOptions"
              :error="condition.MatchTypeError"
            />

            <SearchSelect
              v-model="condition.dbTableId"
              v-if="condition.matchType == 'DB Field' && !condition.group"
              @select="handleConditionDbTableSelect($event, condition)"
              :error="condition.dbTableIdError"
              :options="report?.tables"
              label="DB Table"
              :readonly="isReadOnly"
            />

            <div
              class="flex gap-1"
              v-if="condition.matchType == 'Value' && !condition.group"
            >
              <TextInput
                v-model="condition.value"
                label="Value"
                :error="condition.valueError"
              />
              <button
                id="table-action-remove-table"
                @click="removeCondition(index)"
                class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600 mt-4"
                title="Remvoe Join"
              >
                <IconComponent name="Trash2" />
              </button>
            </div>
            <div
              class="flex gap-1"
              v-if="condition.matchType == 'DB Field' && !condition.group"
            >
              <SearchSelect
                label="DB Field"
                v-model="condition.dbField"
                :options="condition.dbTableFields"
                :error="condition.dbFieldError"
              />
              <button
                id="table-action-remove-table"
                @click="removeCondition(index)"
                class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600 mt-4"
                title="Remvoe Join"
              >
                <IconComponent name="Trash2" />
              </button>
            </div>
            <GridInput
              label="Condition Group"
              v-if="condition.group"
              class="col-span-4"
            >
              <div v-for="(child, cIndex) in condition.childConditions">
                <div
                  class="grid md:grid-cols-4 gap-6"
                  :class="{
                    'border border-gray-300 dark:border-gray-600 p-4 rounded-lg mt-1':
                      condition.childConditions.length > 0,
                  }"
                >
                  <SearchSelect
                    v-model="child.type"
                    :error="child.typeError"
                    :options="ConditionTypeOptions"
                    v-if="cIndex > 0"
                    label="Type"
                    :readonly="isReadOnly"
                  />

                  <SearchSelect
                    v-model="child.tableId"
                    @select="handleConditionTableSelect($event, child)"
                    :error="child.tableError"
                    :options="report?.tables"
                    label="Table"
                    :readonly="isReadOnly"
                  />

                  <SearchSelect
                    label="Field"
                    v-model="child.fieldName"
                    :error="child.fieldNameError"
                    :options="child.tableFields"
                  />
                  <SearchSelect
                    label="Operator"
                    v-model="child.operator"
                    :error="child.operatorError"
                    :options="OperationOptions"
                  />
                  <SearchSelect
                    label="Match Type"
                    v-model="child.matchType"
                    :options="MatchTypeOptions"
                    :error="child.MatchTypeError"
                  />

                  <SearchSelect
                    v-model="child.dbTableId"
                    v-if="child.matchType == 'DB Field'"
                    @select="handleConditionDbTableSelect($event, child)"
                    :error="child.dbTableIdError"
                    :options="report?.tables"
                    label="DB Table"
                    :readonly="isReadOnly"
                  />

                  <div class="flex gap-1" v-if="child.matchType == 'Value'">
                    <TextInput
                      v-model="child.value"
                      label="Value"
                      :error="child.valueError"
                    />
                    <button
                      id="table-action-remove-table"
                      @click="removeChildCondition(condition, cIndex)"
                       v-if="cIndex > 1"
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600 mt-4"
                      title="Remvoe Join"
                    >
                      <IconComponent name="Trash2" />
                    </button>
                  </div>
                  <div class="flex gap-1" v-if="child.matchType == 'DB Field'">
                    <SearchSelect
                      label="DB Field"
                      v-model="child.dbField"
                      :options="child.dbTableFields"
                      :error="child.dbFieldError"
                    />
                    <button
                      id="table-action-remove-table"
                      @click="removeChildCondition(condition, cIndex)"
                      v-if="cIndex > 1"
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600 mt-4"
                      title="Remvoe Join"
                    >
                      <IconComponent name="Trash2" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex justify-end gap-2 mt-2">
                <Buttons
                  caption="Remove Condition"
                  type="danger"
                  icon="Trash2"
                  @click="removeCondition(index)"
                  v-if="mode != 'view'"
                />
                <Buttons
                  caption="Add Condition To Group"
                  type="info"
                  icon="Option"
                  @click="handleAddChildCondition(condition)"
                  v-if="mode != 'view'"
                />
              </div>
            </GridInput>
          </div>
        </div>

        <div class="col-span-4 mt-2">
          <div class="flex justify-end gap-2">
            <Buttons
              caption="Add Condition"
              type="info"
              icon="Option"
              @click="handleAddCondition"
              v-if="mode != 'view'"
            />
          </div>
        </div>
      </GridInput>
    </template>
  </ModalFormTemplate>
</template>
